import { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, Circle, ArrowRight, Building2, MapPin, Users, Target, Plus, X, Calendar, Zap, Lock, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GuidedSetup({ user, firmId, onSetupComplete }) {
  const [loading, setLoading] = useState(true);
  const [setupStatus, setSetupStatus] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    services: [],
    customService: '',
    locations: [],
    customLocation: '',
    targetAudience: '',
    competitors: [{ name: '', website: '' }]
  });

  const SERVICES = [
    'Personal Injury', 'Corporate Law', 'Immigration Law', 'Family Law',
    'Real Estate Law', 'Criminal Defense', 'Estate Planning', 'Employment Law',
    'Intellectual Property', 'Bankruptcy Law', 'Tax Law', 'Healthcare Law'
  ];

  const AUDIENCES = [
    'Individuals', 'Small Businesses', 'Startups', 'Corporate Clients',
    'Property Buyers', 'Families', 'High Net Worth Individuals', 'Enterprise Companies'
  ];

  useEffect(() => {
    fetchSetupStatus();
  }, [firmId]);

  const fetchSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/setup-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupStatus(response.data);
    } catch (err) {
      console.error('Error fetching setup status:', err);
      // Set default status if endpoint not available
      setSetupStatus({
        business_details: true,
        target_markets: true,
        ai_scan_in_progress: true,
        keyword_strategy: false,
        competitors_added: false,
        setup_complete: false
      });
    } finally {
      setLoading(false);
    }
  };

  const addCompetitor = () => {
    if (formData.competitors.length < 5) {
      setFormData({
        ...formData,
        competitors: [...formData.competitors, { name: '', website: '' }]
      });
    }
  };

  const removeCompetitor = (index) => {
    const updated = formData.competitors.filter((_, i) => i !== index);
    setFormData({ ...formData, competitors: updated.length ? updated : [{ name: '', website: '' }] });
  };

  const updateCompetitor = (index, field, value) => {
    const updated = [...formData.competitors];
    updated[index][field] = value;
    setFormData({ ...formData, competitors: updated });
  };

  const toggleService = (service) => {
    const services = formData.services.includes(service)
      ? formData.services.filter(s => s !== service)
      : [...formData.services, service];
    setFormData({ ...formData, services });
  };

  const addCustomService = () => {
    if (formData.customService.trim()) {
      setFormData({
        ...formData,
        services: [...formData.services, formData.customService.trim()],
        customService: ''
      });
    }
  };

  const addLocation = () => {
    if (formData.customLocation.trim()) {
      setFormData({
        ...formData,
        locations: [...formData.locations, formData.customLocation.trim()],
        customLocation: ''
      });
    }
  };

  const removeLocation = (index) => {
    setFormData({
      ...formData,
      locations: formData.locations.filter((_, i) => i !== index)
    });
  };

  const handleSubmitSetup = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save competitors
      const validCompetitors = formData.competitors.filter(c => c.name.trim());
      if (validCompetitors.length > 0) {
        await axios.post(`${API}/competitors/bulk`, {
          competitors: validCompetitors
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Save setup data
      await axios.post(`${API}/guided-setup`, {
        services: formData.services,
        locations: formData.locations,
        target_audience: formData.targetAudience,
        competitors: validCompetitors
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowSetupModal(false);
      fetchSetupStatus();
      if (onSetupComplete) onSetupComplete();
    } catch (err) {
      console.error('Error saving setup:', err);
      alert('Failed to save setup. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  // Don't show if setup is complete
  if (setupStatus?.setup_complete) return null;

  const steps = [
    { id: 1, label: 'Business details submitted', done: setupStatus?.business_details },
    { id: 2, label: 'Target markets received', done: setupStatus?.target_markets },
    { id: 3, label: 'AI visibility scan in progress', done: false, inProgress: setupStatus?.ai_scan_in_progress },
    { id: 4, label: 'Keyword strategy preparing', done: false, inProgress: false }
  ];

  const completedSteps = steps.filter(s => s.done).length;
  const progressPercent = (completedSteps / steps.length) * 100;

  return (
    <>
      {/* Setup Progress Card */}
      <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600 rounded-3xl p-8 text-white soft-shadow-lg mb-8 relative overflow-hidden" data-testid="guided-setup-card">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
        
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-amber-300" />
                <span className="text-white/80 text-sm font-medium">Setup Progress</span>
              </div>
              <h2 className="text-2xl font-bold mb-1" style={{fontFamily: 'Outfit'}}>Complete Your Setup</h2>
              <p className="text-white/70">Finish setup to unlock your full AI visibility report</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold" style={{fontFamily: 'Outfit'}}>{Math.round(progressPercent)}%</div>
              <div className="text-white/60 text-sm">Complete</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-white/20 rounded-full mb-6">
            <div 
              className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Steps checklist */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {steps.map((step) => (
              <div 
                key={step.id}
                className={`flex items-center gap-3 p-3 rounded-xl ${
                  step.done 
                    ? 'bg-emerald-500/20' 
                    : step.inProgress 
                      ? 'bg-amber-500/20'
                      : 'bg-white/10'
                }`}
              >
                {step.done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : step.inProgress ? (
                  <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-white/40 flex-shrink-0" />
                )}
                <span className={`text-sm font-medium ${step.done ? 'text-emerald-300' : step.inProgress ? 'text-amber-300' : 'text-white/60'}`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <button
            onClick={() => setShowSetupModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-white text-violet-600 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid="continue-setup-btn"
          >
            Continue Setup (2 mins)
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Setup Modal */}
      {showSetupModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="setup-modal">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto soft-shadow-lg">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
                  {currentStep === 1 && 'Services & Products'}
                  {currentStep === 2 && 'Target Locations'}
                  {currentStep === 3 && 'Target Audience'}
                  {currentStep === 4 && 'Add Competitors to Track'}
                </h2>
                <p className="text-sm text-slate-500">Step {currentStep} of 4</p>
              </div>
              <button
                onClick={() => setShowSetupModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Step 1: Services */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <p className="text-slate-600">What services should we focus visibility on?</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SERVICES.map((service) => (
                      <button
                        key={service}
                        onClick={() => toggleService(service)}
                        className={`p-3 rounded-xl text-left text-sm font-medium transition-all ${
                          formData.services.includes(service)
                            ? 'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {formData.services.includes(service) && <CheckCircle2 className="w-4 h-4 inline mr-2" />}
                        {service}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.customService}
                      onChange={(e) => setFormData({ ...formData, customService: e.target.value })}
                      onKeyPress={(e) => e.key === 'Enter' && addCustomService()}
                      placeholder="Add other service..."
                      className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                    <button onClick={addCustomService} className="px-4 py-3 bg-slate-800 text-white rounded-xl">
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Locations */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <p className="text-slate-600">Where are your target markets? Add cities, regions, or countries.</p>
                  
                  {formData.locations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {formData.locations.map((loc, idx) => (
                        <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-lg text-sm font-medium">
                          <MapPin className="w-3 h-3" />
                          {loc}
                          <button onClick={() => removeLocation(idx)} className="ml-1 hover:text-red-600">
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={formData.customLocation}
                        onChange={(e) => setFormData({ ...formData, customLocation: e.target.value })}
                        onKeyPress={(e) => e.key === 'Enter' && addLocation()}
                        placeholder="e.g., New York City, California, United States"
                        className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <button onClick={addLocation} className="px-4 py-3 bg-slate-800 text-white rounded-xl">
                      Add
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Target Audience */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <p className="text-slate-600">Who should find your business online?</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {AUDIENCES.map((audience) => (
                      <button
                        key={audience}
                        onClick={() => setFormData({ ...formData, targetAudience: audience })}
                        className={`p-3 rounded-xl text-left text-sm font-medium transition-all ${
                          formData.targetAudience === audience
                            ? 'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-lg'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {formData.targetAudience === audience && <CheckCircle2 className="w-4 h-4 inline mr-2" />}
                        {audience}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Competitors */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <p className="text-slate-600">Add competitors you want to track. We'll compare your AI visibility against them.</p>
                  
                  <div className="space-y-4">
                    {formData.competitors.map((comp, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-600">Competitor {idx + 1}</span>
                          {formData.competitors.length > 1 && (
                            <button onClick={() => removeCompetitor(idx)} className="text-red-500 hover:text-red-700">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={comp.name}
                          onChange={(e) => updateCompetitor(idx, 'name', e.target.value)}
                          placeholder="Competitor Name"
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                        <input
                          type="text"
                          value={comp.website}
                          onChange={(e) => updateCompetitor(idx, 'website', e.target.value)}
                          placeholder="Website URL (optional)"
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                      </div>
                    ))}
                  </div>
                  
                  {formData.competitors.length < 5 && (
                    <button
                      onClick={addCompetitor}
                      className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Competitor
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white p-6 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl font-medium disabled:opacity-50"
              >
                Back
              </button>
              
              {currentStep < 4 ? (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmitSetup}
                  disabled={saving}
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Complete Setup'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Preview Banner Component - Shows when user is on free tier
export function PreviewBanner({ onUpgrade }) {
  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white mb-8" data-testid="preview-banner">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-lg" style={{fontFamily: 'Outfit'}}>You're viewing a preview</h3>
            <p className="text-white/80 text-sm">Unlock full visibility report and competitor analysis</p>
          </div>
        </div>
        <button
          onClick={onUpgrade}
          className="px-6 py-3 bg-white text-orange-600 rounded-xl font-semibold hover:shadow-lg transition-all"
          data-testid="unlock-full-report-btn"
        >
          Unlock Full Report
        </button>
      </div>
    </div>
  );
}

// Locked Section Component
export function LockedSection({ title, description }) {
  return (
    <div className="relative bg-white/70 backdrop-blur-sm rounded-2xl border border-slate-200/50 p-8 text-center soft-shadow" data-testid="locked-section">
      <div className="absolute inset-0 bg-slate-900/5 backdrop-blur-[2px] rounded-2xl flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-bold text-slate-700 mb-2" style={{fontFamily: 'Outfit'}}>{title}</h3>
          <p className="text-slate-500 text-sm">{description}</p>
        </div>
      </div>
      {/* Placeholder content behind blur */}
      <div className="opacity-20 pointer-events-none">
        <div className="h-8 bg-slate-200 rounded-lg w-1/3 mb-4"></div>
        <div className="h-32 bg-slate-100 rounded-xl"></div>
      </div>
    </div>
  );
}

// Competitor Gap Alert Component
export function CompetitorGapAlert({ yourCitations, topCompetitorCitations }) {
  const gap = topCompetitorCitations - yourCitations;
  const multiplier = yourCitations > 0 ? (topCompetitorCitations / yourCitations).toFixed(1) : 'N/A';
  
  return (
    <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-200 rounded-2xl p-6 mb-8" data-testid="competitor-gap-alert">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Target className="w-6 h-6 text-rose-600" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900 mb-1" style={{fontFamily: 'Outfit'}}>Competitor Gap Detected</h3>
          <p className="text-slate-600 mb-3">
            Your business appears in AI answers <strong className="text-rose-600">{yourCitations} times</strong>.
            Competitors are appearing up to <strong className="text-rose-600">{multiplier}x more frequently</strong>.
          </p>
          <button className="text-rose-600 font-medium text-sm hover:underline flex items-center gap-1">
            Unlock full competitor comparison
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Post-Upgrade Call Prompt
export function PostUpgradeCallPrompt() {
  return null;
}
